import { z } from '../../../zod';
import { ObjectIdStringValidationSchema } from '../../../shared/ObjectId';
import {
  CreateRedirectUrlPathSegmentValidationSchema,
  CreateRedirectUrlQueryParameterValidationSchema,
  RedirectUrlPathSegmentValidationSchema,
  RedirectUrlQueryParameterValidationSchema,
  UpdateRedirectUrlPathSegmentValidationSchema,
  UpdateRedirectUrlQueryParameterValidationSchema,
} from '../redirectAndConfirmation';

const buildFunnelStepCheckoutInputValidationSchema = (schemaName: string) =>
  z
    .object({
      type: z.literal('checkout').meta({
        description: 'A funnel step that loads another page as a checkout step.',
        example: 'checkout',
      }),
      order: z.number().nullish().meta({
        description: 'Display order for this step within the funnel.',
        example: 0,
      }),
      enabled: z.boolean().nullish().meta({
        description: 'Whether this funnel step is active.',
        example: true,
      }),
      config: z
        .object({
          pageId: ObjectIdStringValidationSchema.nullish().meta({
            description: 'The checkout or event page shown for this funnel step.',
            example: '507f1f77bcf86cd799439011',
          }),
        })
        .nullish()
        .meta({
          description: 'Configuration for a checkout funnel step.',
          example: { pageId: '507f1f77bcf86cd799439011' },
        }),
    })
    .meta({
      id: schemaName,
      description: 'Loads another page as a step in the funnel.',
      example: {
        type: 'checkout',
        order: 0,
        enabled: true,
        config: { pageId: '507f1f77bcf86cd799439011' },
      },
    });

const CreateFunnelStepCheckoutInputValidationSchema = buildFunnelStepCheckoutInputValidationSchema(
  'CreateFunnelStepCheckoutInput',
);

const UpdateFunnelStepCheckoutInputValidationSchema = buildFunnelStepCheckoutInputValidationSchema(
  'UpdateFunnelStepCheckoutInput',
);

const FunnelStepCheckoutInputValidationSchema =
  buildFunnelStepCheckoutInputValidationSchema('FunnelStepCheckoutInput');

const CreateFunnelStepRedirectInputValidationSchema = z
  .object({
    url: z.string().nullish().meta({
      description: 'Destination URL used when the funnel step action is `redirect`.',
      example: 'https://offers.example.com/post-purchase-upsell',
    }),
    path: z
      .array(CreateRedirectUrlPathSegmentValidationSchema)
      .nullish()
      .meta({
        description:
          'Optional path segments appended to the redirect URL using page, field, or other token values. For field values during create, use `fieldKey`.',
        example: [{ key: 'fields', fieldKey: 'customer-email' }],
      }),
    query: z
      .array(CreateRedirectUrlQueryParameterValidationSchema)
      .nullish()
      .meta({
        description: 'Optional query parameters appended to the redirect URL.',
        example: [{ parameter: 'email', key: 'fields', fieldKey: 'customer-email' }],
      }),
  })
  .meta({
    id: 'CreateFunnelStepRedirectInput',
    description:
      'Redirect target for funnel steps that send the customer to another URL instead of keeping them on the same checkout surface.',
    example: {
      url: 'https://offers.example.com/post-purchase-upsell',
      query: [{ parameter: 'orderId', key: 'orderId', identifier: 'orderId' }],
    },
  });

const FunnelStepRedirectInputValidationSchema = z
  .object({
    url: z.string().nullish().meta({
      description: 'Destination URL used when the funnel step action is `redirect`.',
      example: 'https://offers.example.com/post-purchase-upsell',
    }),
    path: z
      .array(RedirectUrlPathSegmentValidationSchema)
      .nullish()
      .meta({
        description:
          'Optional path segments appended to the redirect URL using page, field, or other token values.',
        example: [{ key: 'orderId', identifier: 'orderId' }],
      }),
    query: z
      .array(RedirectUrlQueryParameterValidationSchema)
      .nullish()
      .meta({
        description: 'Optional query parameters appended to the redirect URL.',
        example: [{ parameter: 'email', key: 'fields', identifier: 'customer-email-field-id' }],
      }),
  })
  .meta({
    id: 'FunnelStepRedirectInput',
    description:
      'Redirect target for funnel steps that send the customer to another URL instead of keeping them on the same checkout surface.',
    example: {
      url: 'https://offers.example.com/post-purchase-upsell',
      query: [{ parameter: 'orderId', key: 'orderId', identifier: 'orderId' }],
    },
  });

const UpdateFunnelStepRedirectInputValidationSchema = z
  .object({
    url: z.string().nullish().meta({
      description: 'Destination URL used when the funnel step action is `redirect`.',
      example: 'https://offers.example.com/post-purchase-upsell',
    }),
    path: z
      .array(UpdateRedirectUrlPathSegmentValidationSchema)
      .nullish()
      .meta({
        description:
          'Optional path segments appended to the redirect URL using page, field, or other token values. For field values during update, use `fieldId`.',
        example: [{ key: 'fields', fieldId: '507f1f77bcf86cd799439011' }],
      }),
    query: z
      .array(UpdateRedirectUrlQueryParameterValidationSchema)
      .nullish()
      .meta({
        description: 'Optional query parameters appended to the redirect URL.',
        example: [{ parameter: 'email', key: 'fields', fieldId: '507f1f77bcf86cd799439011' }],
      }),
  })
  .meta({
    id: 'UpdateFunnelStepRedirectInput',
    description:
      'Redirect target for funnel steps that send the customer to another URL instead of keeping them on the same checkout surface.',
    example: {
      url: 'https://offers.example.com/post-purchase-upsell',
      query: [{ parameter: 'orderId', key: 'orderId', identifier: 'orderId' }],
    },
  });

const buildFunnelStepUpsellInputValidationSchema = (
  redirectSchema:
    | typeof CreateFunnelStepRedirectInputValidationSchema
    | typeof FunnelStepRedirectInputValidationSchema
    | typeof UpdateFunnelStepRedirectInputValidationSchema,
  schemaName: string,
) =>
  z
    .object({
      type: z.literal('upsell').meta({
        description: 'A one-click upsell step shown after a successful purchase.',
        example: 'upsell',
      }),
      order: z.number().nullish().meta({
        description: 'Display order for this step within the funnel.',
        example: 1,
      }),
      enabled: z.boolean().nullish().meta({
        description: 'Whether the upsell step is active.',
        example: true,
      }),
      config: z
        .object({
          action: z.enum(['checkout', 'redirect']).nullish().meta({
            description:
              'How to present the upsell. `checkout` swaps in another checkout or event page on the same surface. `redirect` sends the customer to a URL that contains the upsell.',
            example: 'checkout',
          }),
          pageId: ObjectIdStringValidationSchema.nullish().meta({
            description:
              'The checkout or event page to use as the upsell when `action` is `checkout`. In the dashboard this matches the "Upsell checkout page" picker.',
            example: '507f1f77bcf86cd799439012',
          }),
          redirect: redirectSchema.nullish().meta({
            description: 'Redirect destination used when `action` is `redirect`.',
          }),
        })
        .nullish()
        .meta({
          description: 'Configuration for a one-click upsell step.',
          examples: [
            {
              action: 'checkout',
              pageId: '507f1f77bcf86cd799439012',
            },
            {
              action: 'redirect',
              redirect: {
                url: 'https://offers.example.com/post-purchase-upsell',
              },
            },
          ],
        }),
    })
    .meta({
      id: schemaName,
      description:
        'Defines a post-purchase one-click upsell. This matches the dashboard "One-click upsell" setting and supports showing another page or redirecting to a URL.',
      examples: [
        {
          type: 'upsell',
          order: 1,
          enabled: true,
          config: {
            action: 'checkout',
            pageId: '507f1f77bcf86cd799439012',
          },
        },
        {
          type: 'upsell',
          order: 1,
          enabled: true,
          config: {
            action: 'redirect',
            redirect: {
              url: 'https://offers.example.com/post-purchase-upsell',
            },
          },
        },
      ],
    });

const CreateFunnelStepUpsellInputValidationSchema = buildFunnelStepUpsellInputValidationSchema(
  CreateFunnelStepRedirectInputValidationSchema,
  'CreateFunnelStepUpsellInput',
);

const FunnelStepUpsellInputValidationSchema = buildFunnelStepUpsellInputValidationSchema(
  FunnelStepRedirectInputValidationSchema,
  'FunnelStepUpsellInput',
);

const UpdateFunnelStepUpsellInputValidationSchema = buildFunnelStepUpsellInputValidationSchema(
  UpdateFunnelStepRedirectInputValidationSchema,
  'UpdateFunnelStepUpsellInput',
);

const buildFunnelStepConfirmationInputValidationSchema = (
  pathSchema:
    | typeof CreateRedirectUrlPathSegmentValidationSchema
    | typeof RedirectUrlPathSegmentValidationSchema
    | typeof UpdateRedirectUrlPathSegmentValidationSchema,
  querySchema:
    | typeof CreateRedirectUrlQueryParameterValidationSchema
    | typeof RedirectUrlQueryParameterValidationSchema
    | typeof UpdateRedirectUrlQueryParameterValidationSchema,
  schemaName: string,
  isUpdate = false,
) =>
  z
    .object({
      type: z.literal('confirmation').meta({
        description: 'A funnel step that controls what happens after checkout completes.',
        example: 'confirmation',
      }),
      order: z.number().nullish().meta({
        description: 'Display order for this step within the funnel.',
        example: 2,
      }),
      enabled: z.boolean().nullish().meta({
        description: 'Whether this confirmation step is active.',
        example: true,
      }),
      config: z
        .object({
          action: z.enum(['confirmation', 'checkout', 'redirect']).nullish().meta({
            description:
              'What to show after payment. `confirmation` keeps the standard confirmation view, `checkout` sends the customer to another page, and `redirect` sends them to a URL.',
            example: 'confirmation',
          }),
          redirectPageId: ObjectIdStringValidationSchema.nullish().meta({
            description: 'The page to send the customer to when `action` is `checkout`.',
            example: '507f1f77bcf86cd799439013',
          }),
          redirectUrl: z.string().nullish().meta({
            description: 'The destination URL when `action` is `redirect`.',
            example: 'https://example.com/thank-you',
          }),
          redirectUrlPath: z
            .array(pathSchema)
            .nullish()
            .meta({
              description: isUpdate
                ? 'Optional path values appended to `redirectUrl`. To reference a field value, use `fieldId` (the persisted field ID, from the page detail response) — the create-time `fieldKey` string is rejected.'
                : 'Optional path values appended to `redirectUrl`.',
              example: isUpdate
                ? [{ key: 'fields', fieldId: '507f1f77bcf86cd799439011' }]
                : [{ key: 'orderId', identifier: 'orderId' }],
            }),
          redirectUrlQuery: z
            .array(querySchema)
            .nullish()
            .meta({
              description: isUpdate
                ? 'Optional query parameters appended to `redirectUrl`. To reference a field value, use `fieldId` instead of the create-time `fieldKey`.'
                : 'Optional query parameters appended to `redirectUrl`.',
              example: isUpdate
                ? [{ parameter: 'email', key: 'fields', fieldId: '507f1f77bcf86cd799439011' }]
                : [{ parameter: 'email', key: 'fields', identifier: 'customer-email-field-id' }],
            }),
          customizeCheckoutConfirmation: z.boolean().nullish().meta({
            description: 'Whether to override the default confirmation title and message.',
            example: true,
          }),
          confirmationCheckoutTitle: z.string().nullish().meta({
            description:
              'Custom confirmation title shown when `customizeCheckoutConfirmation` is enabled.',
            example: 'Order confirmed',
          }),
          confirmationCheckoutMessage: z.string().nullish().meta({
            description:
              'Custom confirmation message shown when `customizeCheckoutConfirmation` is enabled.',
            example: '<p>Thanks for your purchase. Check your inbox for the receipt.</p>',
          }),
        })
        .nullish()
        .meta({
          description: 'Configuration for the final confirmation step in the funnel.',
          examples: [
            {
              action: 'confirmation',
              customizeCheckoutConfirmation: true,
              confirmationCheckoutTitle: 'Order confirmed',
              confirmationCheckoutMessage: '<p>Thanks for your purchase.</p>',
            },
            {
              action: 'checkout',
              redirectPageId: '507f1f77bcf86cd799439013',
            },
            {
              action: 'redirect',
              redirectUrl: 'https://example.com/thank-you',
            },
          ],
        }),
    })
    .meta({
      id: schemaName,
      description: 'Controls the post-checkout confirmation step.',
      examples: [
        {
          type: 'confirmation',
          order: 2,
          enabled: true,
          config: {
            action: 'confirmation',
            customizeCheckoutConfirmation: true,
            confirmationCheckoutTitle: 'Order confirmed',
            confirmationCheckoutMessage: '<p>Thanks for your purchase.</p>',
          },
        },
        {
          type: 'confirmation',
          order: 2,
          enabled: true,
          config: {
            action: 'checkout',
            redirectPageId: '507f1f77bcf86cd799439013',
          },
        },
      ],
    });

const CreateFunnelStepConfirmationInputValidationSchema =
  buildFunnelStepConfirmationInputValidationSchema(
    CreateRedirectUrlPathSegmentValidationSchema,
    CreateRedirectUrlQueryParameterValidationSchema,
    'CreateFunnelStepConfirmationInput',
  );

const FunnelStepConfirmationInputValidationSchema =
  buildFunnelStepConfirmationInputValidationSchema(
    RedirectUrlPathSegmentValidationSchema,
    RedirectUrlQueryParameterValidationSchema,
    'FunnelStepConfirmationInput',
  );

const UpdateFunnelStepConfirmationInputValidationSchema =
  buildFunnelStepConfirmationInputValidationSchema(
    UpdateRedirectUrlPathSegmentValidationSchema,
    UpdateRedirectUrlQueryParameterValidationSchema,
    'UpdateFunnelStepConfirmationInput',
    true,
  );

export const CreateFunnelStepInputValidationSchema = z
  .discriminatedUnion('type', [
    CreateFunnelStepCheckoutInputValidationSchema,
    CreateFunnelStepUpsellInputValidationSchema,
    CreateFunnelStepConfirmationInputValidationSchema,
  ])
  .meta({
    id: 'CreateFunnelStepInput',
    description:
      'A single step in a page funnel. Use these to define follow-up checkout steps, one-click upsells, and post-purchase confirmation behavior.',
    examples: [
      {
        type: 'upsell',
        order: 1,
        enabled: true,
        config: {
          action: 'checkout',
          pageId: '507f1f77bcf86cd799439012',
        },
      },
      {
        type: 'confirmation',
        order: 2,
        enabled: true,
        config: {
          action: 'redirect',
          redirectUrl: 'https://example.com/thank-you',
        },
      },
    ],
  });

export const UpdateFunnelStepInputValidationSchema = z
  .discriminatedUnion('type', [
    UpdateFunnelStepCheckoutInputValidationSchema,
    UpdateFunnelStepUpsellInputValidationSchema,
    UpdateFunnelStepConfirmationInputValidationSchema,
  ])
  .meta({
    id: 'UpdateFunnelStepInput',
    description:
      'A single step in a page funnel. Use these to define follow-up checkout steps, one-click upsells, and post-purchase confirmation behavior.',
    examples: [
      {
        type: 'upsell',
        order: 1,
        enabled: true,
        config: {
          action: 'checkout',
          pageId: '507f1f77bcf86cd799439012',
        },
      },
      {
        type: 'confirmation',
        order: 2,
        enabled: true,
        config: {
          action: 'redirect',
          redirectUrl: 'https://example.com/thank-you',
        },
      },
    ],
  });

export const FunnelStepInputValidationSchema = z
  .discriminatedUnion('type', [
    FunnelStepCheckoutInputValidationSchema,
    FunnelStepUpsellInputValidationSchema,
    FunnelStepConfirmationInputValidationSchema,
  ])
  .meta({
    id: 'FunnelStepInput',
    description:
      'A single step in a page funnel. Use these to define follow-up checkout steps, one-click upsells, and post-purchase confirmation behavior.',
    examples: [
      {
        type: 'upsell',
        order: 1,
        enabled: true,
        config: {
          action: 'checkout',
          pageId: '507f1f77bcf86cd799439012',
        },
      },
      {
        type: 'confirmation',
        order: 2,
        enabled: true,
        config: {
          action: 'redirect',
          redirectUrl: 'https://example.com/thank-you',
        },
      },
    ],
  });
